import { useState } from "react"
import { DocumentCheckIcon } from "@heroicons/react/24/outline"
import { Certificate } from "./types"
import { Section, Item, EmptyState } from "./ui"
import { Modal } from "../../components/modal/Modal"

export const CertificateBlock = ({
  items,
  onAdd,
  onDelete,
}: {
  items: Certificate[]
  onAdd: (data: Omit<Certificate, "id">) => void
  onDelete: (id: string) => void
}) => {
  const [open, setOpen] = useState(false)

  const [title, setTitle] = useState("")
  const [issuer, setIssuer] = useState("")
  const [issuedAt, setIssuedAt] = useState("")
  const [link, setLink] = useState("")

  const resetForm = () => {
    setTitle("")
    setIssuer("")
    setIssuedAt("")
    setLink("")
  }

  const handleSave = () => {
    if (!title.trim() || !issuer.trim() || !issuedAt) return

    onAdd({
      title,
      issuer,
      issued_at: issuedAt,
      ...(link.trim() ? { link } : {}),
    })

    resetForm()
    setOpen(false)
  }

  return (
    <>
      <Section
        id="certificates"
        title="Certificates"
        icon={DocumentCheckIcon}
        iconColor="text-emerald-500 bg-emerald-50"
        description="Professional certificates and achievements"
        onAdd={() => setOpen(true)}
      >
        {!items.length && (
          <EmptyState
            icon={DocumentCheckIcon}
            title="No certificates added yet"
            subtitle="Add your certificates to strengthen your profile"
            ctaLabel="Add certificate"
            onAdd={() => setOpen(true)}
          />
        )}

        {items.map((c) => (
          <Item
            key={c.id}
            title={c.title}
            subtitle={formatCertificate(c)}
            link={c.link}
            onDelete={() => onDelete(c.id)}
          />
        ))}
      </Section>

      {/* MODAL */}
      <Modal
        open={open}
        onClose={() => {
          resetForm()
          setOpen(false)
        }}
        title="Add certificate"
      >
        <div className="space-y-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Certificate title"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <input
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            placeholder="Issuer (e.g. Coursera, Google)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <input
            type="date"
            value={issuedAt}
            onChange={(e) => setIssuedAt(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Certificate link (optional)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { resetForm(); setOpen(false) }}
              className="border border-gray-200 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={!title || !issuer || !issuedAt}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 hover:bg-blue-700 transition"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

/* ===================== HELPERS ===================== */

const formatCertificate = (c: Certificate) => {
  const year = new Date(c.issued_at).getFullYear()
  return `${c.issuer} · ${year}`
}
